import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { PortfolioEditorMode } from '@ddt-web/config';
import { ModelPortfolio, ModelPortfolioDirector, WebUserDataConstants } from '@ddt-web/domain';
import { StoreService } from '@ddt-web/store-service';
import { UrlRedirectService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as values from 'lodash/values';
import { combineLatest } from 'rxjs';

@UntilDestroy()
@Component({
	selector: 'ac-new-analysis',
	templateUrl: './new-analysis.component.html',
	styleUrls: ['./new-analysis.component.scss']
})
export class NewAnalysisComponent implements OnInit {
	@Input() public maximumPortfoliosToSelect: number;
	@Input() public maximumChildrenLimit: number;
	@Input() public userPortfolios: any;
	@Input() public partnerPortfolios: any;
	@Input() public selectedPortfolios: Array<ModelPortfolio>;
	@Input() public includeTaxPortfolios: boolean = false;
	@Input() public includeAssetClassPortfolios: boolean = false;
	@Input() public showCreateNewPortfolioButton: boolean = true;
	@Input() public portfolioIdsToHide: string[] = [];
	@Input() public enableFilterByPortfolioType: boolean = true;
	@Input() public enableDeselectAll: boolean = false;
	@Input() public portfolioEditMode?: PortfolioEditorMode;

	@Output() public savedPortfoliosChange: EventEmitter<Array<ModelPortfolio>> = new EventEmitter<Array<ModelPortfolio>>();
	@Output() public onNewPortfolioCreated: EventEmitter<Array<ModelPortfolio>> = new EventEmitter<Array<ModelPortfolio>>();
	@Output() public isChildLimitReached: EventEmitter<boolean> = new EventEmitter();

	public savedPortfolios: Array<ModelPortfolio> = [];
	public isPortfoliosLoading: boolean = true;
	public isPartnerPortfoliosLoading: boolean = true;
	public partnerPortfoliosLoadingMessage: string;

	constructor(
		public contentService: ContentService,
		private storeService: StoreService,
		private urlRedirectService: UrlRedirectService,
		public tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		this.partnerPortfoliosLoadingMessage = this.contentService.get('MY_PORTFOLIOS_PARTNER_PORTFOLIO_LOADING_MESSAGE', 'Loading Partner Portfolios');

		const savedPortfolios$ = this.storeService.selectAll(this.userPortfolios);
		const partnerPortfolios$ = this.storeService.selectAll(this.partnerPortfolios);

		combineLatest(savedPortfolios$, partnerPortfolios$)
			.pipe(untilDestroyed(this))
			.subscribe(([savedPortfolios, partnerPortfolios]) => {
				if (savedPortfolios && savedPortfolios['entities']) {
					const entities: Array<any> = Object.values(savedPortfolios.entities);
					this.savedPortfolios = entities
						.map(portfolio => new ModelPortfolioDirector().construct(portfolio))
						.filter(portfolio => {
							const isPortfolio = portfolio.type === WebUserDataConstants.TYPE.PORTFOLIO;
							const isPortfolioLike = this.includeTaxPortfolios && portfolio.type === WebUserDataConstants.TYPE.PORTFOLIO_LIKE
								&& portfolio.subType === WebUserDataConstants.SUBTYPE.TAX_REPORT;
							const isAssetClassPorts = this.includeAssetClassPortfolios && portfolio.isAssetClassOnly;
							const isPartnerModel: boolean = portfolio.subType === WebUserDataConstants.SUBTYPE.PARTNER_MODEL;
							const shouldHidePortfolio: boolean = this.portfolioIdsToHide.includes(portfolio.uniqueId);
							return (isPortfolio || isPortfolioLike || isAssetClassPorts) && !isPartnerModel && !shouldHidePortfolio;
						});
					if (values(partnerPortfolios.entities).length) {
						this.savedPortfolios = [...this.savedPortfolios, ...values(partnerPortfolios.entities)];
					}
					this.savedPortfoliosChange.emit(this.savedPortfolios);
					this.isPortfoliosLoading = savedPortfolios.isLoading;
					this.isPartnerPortfoliosLoading = partnerPortfolios.isLoading;
				}
			});
	}

	public openCreateNewPortfolio(): void {
		this.urlRedirectService.redirectToPortfolioEditorView({ mode: this.portfolioEditMode });
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent('new-analysis:button:create new portfolio', {}));
	}

	public checkChildrenValidity($event): void {
		this.isChildLimitReached.emit($event);
	}
}
